import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Users, FileText, DollarSign, TrendingUp, CheckCircle, XCircle, Pin, PauseCircle, UserPlus, Shield, Calendar, Wallet } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [ads, setAds] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState({ agents: [], summary: {} });
  const [selectedAd, setSelectedAd] = useState(null);
  const [pinForm, setPinForm] = useState({ ad_id: "", is_global: false, city_ids: [] });
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ user_id: "", new_password: "" });
  const [supportContact, setSupportContact] = useState({ support_whatsapp: "", support_email: "" });
  const [newAgentForm, setNewAgentForm] = useState({ name: "", email: "", mobile: "", password: "" });
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [freeAdsLimit, setFreeAdsLimit] = useState(2);
  const [savingFreeAdsLimit, setSavingFreeAdsLimit] = useState(false);
  const [freeAdsAsTop, setFreeAdsAsTop] = useState(false);
  const [savingFreeAdsAsTop, setSavingFreeAdsAsTop] = useState(false);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTargetUser, setEmailTargetUser] = useState(null);
  const [emailForm, setEmailForm] = useState({ new_email: "" });

  useEffect(() => {
    loadData();
    if (user?.role === 'super_admin') {
      loadSupportContact();
      loadBranding();
      loadAgents();
      loadAgentPerformance();
      loadFreeAdsLimit();
      loadFreeAdsAsTop();
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, adsRes, usersRes, citiesRes] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/ads"),
        api.get("/admin/users"),
        api.get("/cities"),
      ]);
      setStats(statsRes.data || {});
      setAds(adsRes.data || []);
      setUsers(usersRes.data || []);
      setCities(citiesRes.data || []);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      console.error("Error details:", error.response?.data);
      setError(error.response?.data?.detail || "Failed to load dashboard data");
      // Set empty defaults on error
      setStats({});
      setAds([]);
      setUsers([]);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await api.get("/admin/agents");
      setAgents(response.data || []);
    } catch (error) {
      console.error("Failed to load agents:", error);
    }
  };

  const loadAgentPerformance = async () => {
    try {
      const response = await api.get("/admin/agent-performance");
      setAgentPerformance(response.data || { agents: [], summary: {} });
    } catch (error) {
      console.error("Failed to load agent performance:", error);
    }
  };

  const loadSupportContact = async () => {
    try {
      const response = await api.get("/admin/support-contact");
      setSupportContact(response.data);
    } catch (error) {
      console.error("Failed to load support contact:", error);
    }
  };

  const loadFreeAdsLimit = async () => {
    try {
      const response = await api.get("/admin/free-ads-limit");
      setFreeAdsLimit(response.data.free_ads_limit || 2);
    } catch (error) {
      console.error("Failed to load free ads limit:", error);
    }
  };

  // Branding state
  const [brandingForm, setBrandingForm] = useState({
    site_name: "uplino.in",
    tagline: "",
    logo_url: "",
    small_logo_url: "",
    favicon_url: "",
    support_email: "",
  });
  const [savingBranding, setSavingBranding] = useState(false);

  const loadBranding = async () => {
    try {
      const res = await api.get("/admin/site-branding");
      setBrandingForm({
        site_name: res.data.site_name || "uplino.in",
        tagline: res.data.tagline || "",
        logo_url: res.data.logo_url || "",
        small_logo_url: res.data.small_logo_url || "",
        favicon_url: res.data.favicon_url || "",
        support_email: res.data.support_email || "",
      });
    } catch (e) {
      // ignore
    }
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      await api.post("/admin/site-branding", brandingForm);
      toast.success("Branding updated");
      loadBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update branding");
    } finally {
      setSavingBranding(false);
    }
  };

  const loadFreeAdsAsTop = async () => {
    try {
      const response = await api.get("/admin/free-ads-as-top");
      setFreeAdsAsTop(response.data.free_ads_as_top || false);
    } catch (error) {
      console.error("Failed to load free ads as top setting:", error);
    }
  };

  const handleToggleFreeAdsAsTop = async () => {
    setSavingFreeAdsAsTop(true);
    try {
      const newValue = !freeAdsAsTop;
      await api.patch("/admin/free-ads-as-top", { enabled: newValue });
      setFreeAdsAsTop(newValue);
      toast.success(newValue ? "Free ads will now appear as TOP ads!" : "Free ads display reverted to normal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update setting");
    } finally {
      setSavingFreeAdsAsTop(false);
    }
  };

  const handleUpdateFreeAdsLimit = async () => {
    setSavingFreeAdsLimit(true);
    try {
      await api.patch("/admin/free-ads-limit", { limit: freeAdsLimit });
      toast.success(`Free ads limit updated to ${freeAdsLimit}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update free ads limit");
    } finally {
      setSavingFreeAdsLimit(false);
    }
  };

  const handleChangePassword = async (userId) => {
    const newPassword = prompt("Enter new password:");
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await api.post("/admin/users/change-password", {
        user_id: userId,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const handleChangeEmail = async (userObj) => {
    setEmailTargetUser(userObj);
    setEmailForm({ new_email: userObj?.email || "" });
    setEmailModalOpen(true);
  };

  const handleSaveEmail = async () => {
    const newEmail = (emailForm.new_email || "").trim();
    if (!newEmail) {
      toast.error("Please enter an email");
      return;
    }

    try {
      await api.post(`/admin/users/${emailTargetUser.id}/change-email`, { new_email: newEmail });
      toast.success("Email updated");
      setEmailModalOpen(false);
      setEmailTargetUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    }
  };


  const handleUpdateSupportContact = async () => {
    if (!supportContact.support_whatsapp || !supportContact.support_telegram) {
      toast.error("Please fill in WhatsApp and Telegram fields");
      return;
    }

    try {
      await api.post("/admin/support-contact", {
        support_whatsapp: supportContact.support_whatsapp,
        support_telegram: supportContact.support_telegram,
        support_email: supportContact.support_email || "support@uplino.in"
      });
      toast.success("Support contact updated successfully");
    } catch (error) {
      toast.error("Failed to update support contact");
    }
  };

  const handleAdStatusUpdate = async (adId, status) => {
    try {
      await api.patch(`/admin/ads/${adId}/status?new_status=${status}`);
      toast.success("Ad status updated");
      loadData();
    } catch (error) {
      toast.error("Failed to update ad status");
    }
  };

  const handleUserSuspend = async (userId, suspend) => {
    try {
      await api.patch(`/admin/users/${userId}/suspend?suspend=${suspend}`);
      toast.success(suspend ? "User suspended" : "User activated");
      loadData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleChangeRole = async (userId, currentRole) => {
    // Show options based on current role
    let newRole = null;
    
    if (currentRole === 'advertiser') {
      const confirmed = window.confirm("Promote this user to Agent? They will be able to top-up user wallets.");
      if (confirmed) newRole = 'agent';
    } else if (currentRole === 'agent') {
      const choice = window.confirm("Change role to Advertiser? Click OK for Advertiser, Cancel to keep as Agent.");
      if (choice) newRole = 'advertiser';
    } else if (currentRole === 'sub_admin') {
      const choice = window.prompt("Enter new role: 'agent' or 'advertiser'");
      if (choice === 'agent' || choice === 'advertiser') newRole = choice;
    }
    
    if (!newRole) return;
    
    try {
      await api.post("/admin/users/change-role", {
        user_id: userId,
        new_role: newRole
      });
      toast.success(`User role changed to ${newRole}`);
      loadData();
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change user role");
    }
  };

  const handlePinAd = async () => {
    try {
      await api.post("/admin/ads/pin", pinForm);
      toast.success("Ad pinned successfully");
      setPinForm({ ad_id: "", is_global: false, city_ids: [] });
      loadData();
    } catch (error) {
      toast.error("Failed to pin ad");
    }
  };

  const handleUnpinAd = async (adId) => {
    try {
      await api.delete(`/admin/ads/unpin/${adId}`);
      toast.success("Ad unpinned");
      loadData();
    } catch (error) {
      toast.error("Failed to unpin ad");
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!newAgentForm.name || !newAgentForm.email || !newAgentForm.mobile || !newAgentForm.password) {
      toast.error("Please fill all fields");
      return;
    }
    if (newAgentForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setCreatingAgent(true);
    try {
      await api.post("/admin/agents/create", newAgentForm);
      toast.success("Agent created successfully");
      setNewAgentForm({ name: "", email: "", mobile: "", password: "" });
      loadAgents();
      loadData(); // Refresh users list too
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agent");
    } finally {
      setCreatingAgent(false);
    }
  };

  const handleSuspendAgent = async (agentId, suspend) => {
    try {
      await api.patch(`/admin/agents/${agentId}/suspend?suspend=${suspend}`);
      toast.success(suspend ? "Agent suspended" : "Agent activated");
      loadAgents();
      loadData();
    } catch (error) {
      toast.error("Failed to update agent status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
            <p className="text-red-400 text-xl mb-4">{error}</p>
            <Button
              onClick={loadData}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Retry Loading Dashboard
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
            Admin Dashboard
          </h1>
          <p className="text-gray-400">Manage your platform</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Users</p>
                <p className="text-3xl font-bold text-white">{stats.total_users}</p>
              </div>
              <Users className="w-10 h-10 text-blue-400" />
            </div>
          </Card>

          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Ads</p>
                <p className="text-3xl font-bold text-white">{stats.total_ads}</p>
              </div>
              <FileText className="w-10 h-10 text-green-400" />
            </div>
          </Card>

          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pending Approval</p>
                <p className="text-3xl font-bold text-white">{stats.pending_ads}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-yellow-400" />
            </div>
          </Card>

          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-white">₹{(stats.total_revenue || 0).toLocaleString('en-IN')}</p>
              </div>
              <DollarSign className="w-10 h-10 text-purple-400" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-emerald-600/20 to-emerald-900/20 backdrop-blur-sm border-emerald-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 text-sm">Total Wallet Balance</p>
                <p className="text-3xl font-bold text-white">₹{(stats.total_wallet_balance || 0).toLocaleString('en-IN')}</p>
                <p className="text-emerald-400 text-xs mt-1">Across all users</p>
              </div>
              <Wallet className="w-10 h-10 text-emerald-400" />
            </div>
          </Card>
        </div>

        {/* Agent Performance Summary (Super Admin Only) */}
        {user?.role === 'super_admin' && agentPerformance.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-purple-600/20 to-purple-900/20 backdrop-blur-sm border-purple-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-300 text-sm">Total Agents</p>
                  <p className="text-3xl font-bold text-white">{agentPerformance.summary.total_agents || 0}</p>
                </div>
                <Shield className="w-10 h-10 text-purple-400" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-600/20 to-green-900/20 backdrop-blur-sm border-green-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-300 text-sm">Today&apos;s Top-ups</p>
                  <p className="text-3xl font-bold text-white">{agentPerformance.summary.today_topups || 0}</p>
                  <p className="text-green-400 text-sm">₹{agentPerformance.summary.today_amount || 0}</p>
                </div>
                <Calendar className="w-10 h-10 text-green-400" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-600/20 to-blue-900/20 backdrop-blur-sm border-blue-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-300 text-sm">Total Top-ups (All Time)</p>
                  <p className="text-3xl font-bold text-white">{agentPerformance.summary.total_topups || 0}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-blue-400" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-yellow-600/20 to-yellow-900/20 backdrop-blur-sm border-yellow-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-300 text-sm">Total Amount (All Time)</p>
                  <p className="text-3xl font-bold text-white">₹{agentPerformance.summary.total_amount || 0}</p>
                </div>
                <DollarSign className="w-10 h-10 text-yellow-400" />
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="ads" className="w-full">
          <TabsList className="bg-white/5 border-white/10">
            <TabsTrigger value="ads" data-testid="admin-ads-tab">Ads Management</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab">Users</TabsTrigger>
            {user?.role === 'super_admin' && (
              <>
                <TabsTrigger value="agents" data-testid="admin-agents-tab">Agents</TabsTrigger>
                <TabsTrigger value="branding" data-testid="admin-branding-tab">Branding</TabsTrigger>
                <TabsTrigger value="pin" data-testid="admin-pin-tab">Pin Ads</TabsTrigger>
                <TabsTrigger value="settings" data-testid="admin-settings-tab">Settings</TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Ads Management */}
          <TabsContent value="ads">
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
              <h2 className="text-2xl font-bold text-white mb-6">All Ads</h2>
              <div className="space-y-4">
                {ads.map((ad) => (
                  <Card key={ad.id} className="p-4 bg-white/5 border-white/10">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-white">{ad.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs ${
                            ad.status === 'approved' ? 'bg-green-600/20 text-green-400' :
                            ad.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400' :
                            ad.status === 'rejected' ? 'bg-red-600/20 text-red-400' :
                            'bg-gray-600/20 text-gray-400'
                          }`}>
                            {ad.status}
                          </span>
                          {ad.is_global_pinned && (
                            <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded">
                              Global Pin
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm line-clamp-2 mb-2">{ad.description}</p>
                        <p className="text-gray-500 text-xs">ID: {ad.id}</p>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        {ad.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleAdStatusUpdate(ad.id, 'approved')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleAdStatusUpdate(ad.id, 'rejected')}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {ad.status === 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => handleAdStatusUpdate(ad.id, 'paused')}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10"
                          >
                            <PauseCircle className="w-4 h-4 mr-1" />
                            Pause
                          </Button>
                        )}
                        {(ad.is_global_pinned || ad.pinned_cities?.length > 0) && user?.role === 'super_admin' && (
                          <Button
                            size="sm"
                            onClick={() => handleUnpinAd(ad.id)}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10"
                          >
                            Unpin
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Users Management */}
          <TabsContent value="users">
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
              <h2 className="text-2xl font-bold text-white mb-6">All Users</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-gray-400 py-3 px-4">Name</th>
                      <th className="text-left text-gray-400 py-3 px-4">Email</th>
                      <th className="text-left text-gray-400 py-3 px-4">Mobile</th>
                      <th className="text-left text-gray-400 py-3 px-4">Role</th>
                      <th className="text-right text-gray-400 py-3 px-4">Wallet Balance</th>
                      <th className="text-left text-gray-400 py-3 px-4">Status</th>
                      <th className="text-left text-gray-400 py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-white/10">
                        <td className="text-white py-3 px-4">{u.name}</td>
                        <td className="text-gray-400 py-3 px-4">{u.email}</td>
                        <td className="text-gray-400 py-3 px-4">{u.mobile}</td>
                        <td className="text-gray-400 py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            u.role === 'agent' ? 'bg-purple-600/20 text-purple-400' :
                            u.role === 'sub_admin' ? 'bg-orange-600/20 text-orange-400' :
                            u.role === 'super_admin' ? 'bg-red-600/20 text-red-400' :
                            'bg-blue-600/20 text-blue-400'
                          }`}>
                            {u.role === 'agent' ? 'Agent' : 
                             u.role === 'sub_admin' ? 'Sub Admin' : 
                             u.role === 'super_admin' ? 'Super Admin' :
                             u.role === 'advertiser' ? 'Advertiser' : u.role}
                          </span>
                        </td>
                        <td className="text-right py-3 px-4">
                          <span className={`font-semibold ${
                            (u.wallet_balance || 0) > 0 ? 'text-green-400' : 'text-gray-400'
                          }`}>
                            ₹{(u.wallet_balance || 0).toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            u.is_active ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                          }`}>
                            {u.is_active ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {u.role !== 'super_admin' && (
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => handleUserSuspend(u.id, !u.is_active)}
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/10"
                              >
                                {u.is_active ? 'Suspend' : 'Activate'}
                              </Button>

          <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
            <DialogContent className="bg-slate-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle>Change User Email</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-sm text-gray-400">
                  User: <span className="text-white">{emailTargetUser?.name}</span>
                </div>
                <Input
                  type="email"
                  value={emailForm.new_email}
                  onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={() => setEmailModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveEmail}>
                    Save
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

                              {user?.role === 'super_admin' && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleChangeRole(u.id, u.role)}
                                    variant="outline"
                                    className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                                    data-testid={`change-role-${u.id}`}
                                  >
                                    {u.role === 'advertiser' ? 'Make Agent' : 'Change Role'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleChangeEmail(u)}
                                    variant="outline"
                                    className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
                                  >
                                    Email
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleChangePassword(u.id)}
                                    variant="outline"
                                    className="border-blue-600 text-blue-400 hover:bg-blue-600/10"
                                  >
                                    Password
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Agents Management (Super Admin Only) */}
          {user?.role === 'super_admin' && (
            <TabsContent value="agents">
              <div className="space-y-6">
                {/* Create New Agent */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <UserPlus className="w-6 h-6" />
                    Create New Agent
                  </h2>
                  <form onSubmit={handleCreateAgent} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Agent Name *</label>
                        <Input
                          type="text"
                          placeholder="Enter agent name"
                          value={newAgentForm.name}
                          onChange={(e) => setNewAgentForm({ ...newAgentForm, name: e.target.value })}
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                          data-testid="create-agent-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                        <Input
                          type="email"
                          placeholder="agent@example.com"
                          value={newAgentForm.email}
                          onChange={(e) => setNewAgentForm({ ...newAgentForm, email: e.target.value })}
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                          data-testid="create-agent-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Mobile *</label>
                        <Input
                          type="tel"
                          placeholder="9876543210"
                          value={newAgentForm.mobile}
                          onChange={(e) => setNewAgentForm({ ...newAgentForm, mobile: e.target.value })}
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                          data-testid="create-agent-mobile"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Password *</label>
                        <Input
                          type="password"
                          placeholder="Min 6 characters"
                          value={newAgentForm.password}
                          onChange={(e) => setNewAgentForm({ ...newAgentForm, password: e.target.value })}
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                          data-testid="create-agent-password"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={creatingAgent}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="create-agent-submit"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {creatingAgent ? "Creating..." : "Create Agent"}
                    </Button>
                  </form>
                </Card>

                {/* Agents List with Performance */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Shield className="w-6 h-6" />
                    Agent Performance ({agentPerformance.agents?.length || 0})
                  </h2>
                  {(!agentPerformance.agents || agentPerformance.agents.length === 0) ? (
                    <p className="text-gray-400 text-center py-8">No agents created yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left text-gray-400 py-3 px-4">Agent</th>
                            <th className="text-left text-gray-400 py-3 px-4">Contact</th>
                            <th className="text-left text-gray-400 py-3 px-4">Status</th>
                            <th className="text-center text-gray-400 py-3 px-4">Today Top-ups</th>
                            <th className="text-center text-gray-400 py-3 px-4">Today Amount</th>
                            <th className="text-center text-gray-400 py-3 px-4">Total Top-ups</th>
                            <th className="text-center text-gray-400 py-3 px-4">Total Amount</th>
                            <th className="text-left text-gray-400 py-3 px-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentPerformance.agents.map((agent) => (
                            <tr key={agent.id} className="border-b border-white/10 hover:bg-white/5">
                              <td className="text-white py-3 px-4 font-medium">{agent.name}</td>
                              <td className="py-3 px-4">
                                <div className="text-gray-400 text-sm">{agent.email}</div>
                                <div className="text-gray-500 text-xs">{agent.mobile}</div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 text-xs rounded ${
                                  agent.is_active !== false ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                                }`}>
                                  {agent.is_active !== false ? 'Active' : 'Suspended'}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-green-400 font-semibold">{agent.today_topups || 0}</span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-green-400 font-semibold">₹{agent.today_amount || 0}</span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-blue-400 font-semibold">{agent.total_topups || 0}</span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="text-yellow-400 font-semibold">₹{agent.total_amount || 0}</span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleSuspendAgent(agent.id, agent.is_active !== false)}
                                    variant="outline"
                                    className={agent.is_active !== false
                                      ? "border-red-600 text-red-400 hover:bg-red-600/10" 
                                      : "border-green-600 text-green-400 hover:bg-green-600/10"
                                    }
                                    data-testid={`agent-suspend-${agent.id}`}
                                  >
                                    {agent.is_active !== false ? 'Suspend' : 'Activate'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleChangePassword(agent.id)}
                                    variant="outline"
                                    className="border-blue-600 text-blue-400 hover:bg-blue-600/10"
                                    data-testid={`agent-change-password-${agent.id}`}
                                  >
                                    Password
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* Agent Permissions Info */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <h2 className="text-xl font-bold text-white mb-4">Agent Permissions</h2>
                  <div className="bg-blue-600/20 border border-blue-500/50 rounded-lg p-4">
                    <ul className="text-sm text-gray-300 space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span> Search users by email/mobile
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span> Top-up user wallets
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-red-400">✗</span> Cannot view or manage ads
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-red-400">✗</span> Cannot manage other users
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-red-400">✗</span> Cannot access admin settings
                      </li>
                    </ul>
                  </div>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Pin Ads (Super Admin Only) */}
          {user?.role === 'super_admin' && (
            <TabsContent value="pin">
              <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Pin Ad</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Select Ad</label>
                    <Select
                      value={pinForm.ad_id}
                      onValueChange={(value) => setPinForm({ ...pinForm, ad_id: value })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select an ad" />
                      </SelectTrigger>
                      <SelectContent>
                        {ads.filter(ad => ad.status === 'approved').map((ad) => (
                          <SelectItem key={ad.id} value={ad.id}>
                            {ad.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Pin Type</label>
                    <Select
                      value={pinForm.is_global ? "global" : "city"}
                      onValueChange={(value) => setPinForm({ ...pinForm, is_global: value === "global", city_ids: [] })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="global">Global Pin (All Cities)</SelectItem>
                        <SelectItem value="city">City-specific Pin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {!pinForm.is_global && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Select Cities</label>
                      <div className="max-h-60 overflow-y-auto bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pinForm.city_ids.includes("INDIA_ALL")}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPinForm({ ...pinForm, city_ids: ["INDIA_ALL"] });
                                } else {
                                  setPinForm({ ...pinForm, city_ids: [] });
                                }
                              }}
                              className="rounded"
                            />
                            <span className="text-sm text-white">India (All Cities)</span>
                          </label>

                          {cities.map((city) => (
                            <label key={city.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pinForm.city_ids.includes(city.id)}
                                onChange={(e) => {
                                  if (pinForm.city_ids.includes("INDIA_ALL")) return;
                                  if (e.target.checked) {
                                    setPinForm({ ...pinForm, city_ids: [...pinForm.city_ids, city.id] });
                                  } else {
                                    setPinForm({ ...pinForm, city_ids: pinForm.city_ids.filter(id => id !== city.id) });
                                  }
                                }}
                                className="rounded"
                              />
                              <span className="text-sm text-white">{city.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handlePinAd}
                    disabled={!pinForm.ad_id || (!pinForm.is_global && pinForm.city_ids.length === 0)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    <Pin className="w-4 h-4 mr-2" />
                    Pin Ad
                  </Button>
                </div>
              </Card>
            </TabsContent>
          )}

          {/* Branding (Super Admin Only) */}
          {user?.role === 'super_admin' && (
            <TabsContent value="branding">
              <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Site Branding</h2>
                <p className="text-gray-400 mb-6">
                  Customize your site's branding and appearance
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Site Name
                    </label>
                    <Input
                      type="text"
                      placeholder="uplino.in"
                      value={brandingForm.site_name || ""}
                      onChange={(e) => setBrandingForm({ ...brandingForm, site_name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tagline
                    </label>
                    <Input
                      type="text"
                      placeholder="Your site tagline"
                      value={brandingForm.tagline || ""}
                      onChange={(e) => setBrandingForm({ ...brandingForm, tagline: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Logo URL
                    </label>
                    <Input
                      type="url"
                      placeholder="https://example.com/logo.png"
                      value={brandingForm.logo_url || ""}
                      onChange={(e) => setBrandingForm({ ...brandingForm, logo_url: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Small Logo URL
                    </label>
                    <Input
                      type="url"
                      placeholder="https://example.com/small-logo.png"
                      value={brandingForm.small_logo_url || ""}
                      onChange={(e) => setBrandingForm({ ...brandingForm, small_logo_url: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Favicon URL
                    </label>
                    <Input
                      type="url"
                      placeholder="https://example.com/favicon.ico"
                      value={brandingForm.favicon_url || ""}
                      onChange={(e) => setBrandingForm({ ...brandingForm, favicon_url: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Support Email
                    </label>
                    <Input
                      type="email"
                      placeholder="support@example.com"
                      value={brandingForm.support_email || ""}
                      onChange={(e) => setBrandingForm({ ...brandingForm, support_email: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
                <Button
                  onClick={saveBranding}
                  disabled={savingBranding}
                  className="bg-blue-600 hover:bg-blue-700 text-white mt-4"
                >
                  {savingBranding ? "Saving..." : "Save Branding"}
                </Button>
              </Card>
            </TabsContent>
          )}

          {/* Settings (Super Admin Only) */}
          {user?.role === 'super_admin' && (

            <TabsContent value="settings">
              <div className="space-y-6">
                {/* Support Contact */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Support Contact Settings</h2>
                  <p className="text-gray-400 mb-6">
                    Update the contact information displayed on the Contact Us page and wallet top-up requests
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        WhatsApp Number
                      </label>
                      <Input
                        type="text"
                        placeholder="+91-XXXXXXXXXX"
                        value={supportContact.support_whatsapp || ""}
                        onChange={(e) => setSupportContact({ ...supportContact, support_whatsapp: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="support-whatsapp-input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Enter with country code (e.g., +919876543210)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Telegram Contact
                      </label>
                      <Input
                        type="text"
                        placeholder="uplino_support or 919876543210"
                        value={supportContact.support_telegram || ""}
                        onChange={(e) => {
                          // Remove @ if user adds it
                          let value = e.target.value.replace(/^@/, '');
                          setSupportContact({ ...supportContact, support_telegram: value });
                        }}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="support-telegram-input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Enter username (without @) or mobile number</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        placeholder="support@uplino.in"
                        value={supportContact.support_email || ""}
                        onChange={(e) => setSupportContact({ ...supportContact, support_email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="support-email-input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Email for support inquiries</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleUpdateSupportContact}
                    className="bg-green-600 hover:bg-green-700 text-white mt-4"
                    data-testid="save-support-contact-btn"
                  >
                    Save Support Contact
                  </Button>
                </Card>

                {/* Free Ads Limit */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Free Ads Settings</h2>
                  <p className="text-gray-400 mb-6">
                    Control how many free ads each advertiser can post. This limit applies globally to all advertisers.
                  </p>
                  <div className="flex items-end gap-4">
                    <div className="flex-1 max-w-xs">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Free Ads Limit (per user)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={freeAdsLimit}
                        onChange={(e) => setFreeAdsLimit(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="free-ads-limit-input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Enter a value between 0-100</p>
                    </div>
                    <Button
                      onClick={handleUpdateFreeAdsLimit}
                      disabled={savingFreeAdsLimit}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="save-free-ads-limit-btn"
                    >
                      {savingFreeAdsLimit ? "Saving..." : "Save Limit"}
                    </Button>
                  </div>
                  <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      <strong>Note:</strong> Users who have exhausted their free ads and have no wallet balance will not be able to post new ads. Free ads are limited to 1 city only.
                    </p>
                  </div>
                </Card>

                {/* Free Ads as Top Ads Toggle */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-white mb-2">Free Ads as TOP Ads</h2>
                      <p className="text-gray-400 mb-4">
                        When enabled, all free ads will appear like premium TOP ads across the entire website:
                      </p>
                      <ul className="text-gray-400 text-sm space-y-1 mb-4">
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Full unblurred photos (no lock icon)
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Contact buttons visible (Call, WhatsApp, Telegram)
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          TOP badge displayed on all free ads
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Contact info visible on ad detail page
                        </li>
                      </ul>
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        freeAdsAsTop 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${freeAdsAsTop ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                        {freeAdsAsTop ? 'Currently ENABLED' : 'Currently DISABLED'}
                      </div>
                    </div>
                    <div className="ml-6">
                      <Button
                        onClick={handleToggleFreeAdsAsTop}
                        disabled={savingFreeAdsAsTop}
                        className={`min-w-[120px] ${
                          freeAdsAsTop 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-green-600 hover:bg-green-700'
                        } text-white`}
                        data-testid="toggle-free-ads-as-top-btn"
                      >
                        {savingFreeAdsAsTop ? "Saving..." : (freeAdsAsTop ? "Disable" : "Enable")}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <p className="text-purple-400 text-sm">
                      <strong>SEO Tip:</strong> Enable this feature after your website ranks on Google to show premium-looking ads to visitors, encouraging them to use your platform.
                    </p>
                  </div>
                </Card>

                {/* Change Own Password */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Change Your Password</h2>
                  <p className="text-gray-400 mb-6">
                    Update your admin account password
                  </p>
                  <Button
                    onClick={() => handleChangePassword(user.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Change My Password
                  </Button>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
